import type { GridApi } from 'ag-grid-enterprise';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseBlotterTableProps } from './types';

type UseInitialGridLoadingProps<TRow extends object> = {
  dataObservable: UseBlotterTableProps<TRow>['dataObservable'];
  dataState: Set<string>;
  gridOptions: UseBlotterTableProps<TRow>['gridOptions'];
};

/**
 * Coordinates BlotterTable's initial loading state with AG Grid v35's `activeOverlay` behavior.
 *
 * `syncNoRowsOverlay` drives the no-rows overlay through `activeOverlay`, but it must not show
 * no-rows while the grid is still waiting on its first data emission. Keeping this here lets
 * `useBlotterTable` derive loading from its observable lifecycle without forcing every consumer
 * to manually pass `gridOptions.loading`.
 */
export function useInitialGridLoading<TRow extends object>({
  dataObservable,
  dataState,
  gridOptions,
}: UseInitialGridLoadingProps<TRow>) {
  const controlsInitialLoading = gridOptions?.loading == null;
  const [isInitialLoading, setIsInitialLoading] = useState(() => controlsInitialLoading && dataObservable != null);

  useEffect(() => {
    if (controlsInitialLoading) {
      setIsInitialLoading(dataObservable != null && dataState.size === 0);
    }
  }, [controlsInitialLoading, dataObservable, dataState]);

  const startInitialLoading = useCallback(
    (api?: GridApi<TRow>) => {
      if (controlsInitialLoading && dataState.size === 0) {
        setIsInitialLoading(true);
        api?.setGridOption('loading', true);
      }
    },
    [controlsInitialLoading, dataState]
  );

  const clearInitialLoading = useCallback(
    (api?: GridApi<TRow>) => {
      if (controlsInitialLoading) {
        setIsInitialLoading(false);
        api?.setGridOption('loading', false);
      }
    },
    [controlsInitialLoading]
  );

  const gridOptionsWithLoading = useMemo(() => {
    if (!controlsInitialLoading) {
      return gridOptions;
    }

    return {
      ...gridOptions,
      loading: isInitialLoading,
    };
  }, [controlsInitialLoading, gridOptions, isInitialLoading]);

  return {
    clearInitialLoading,
    controlsInitialLoading,
    gridOptionsWithLoading,
    startInitialLoading,
  };
}
